const COMMON_OS_SCALES = [2, 1.25, 1.5, 1, 1.75, 2.25, 2.5, 3] as const;

const BROWSER_ZOOM_PERCENTS = [25, 33, 50, 67, 75, 80, 90, 100, 110, 125, 150, 175, 200, 250, 300, 400, 500] as const;

// Error dominates so inexact DPR matches lose. OS rank lets Retina-at-150%
// (DPR 3) beat 3x-at-100%, while 1x-at-100% still beats 2x-at-50%.
const DPR_ERROR_WEIGHT = 1000;
const OS_RANK_WEIGHT = 10;
const COST_TIE_EPSILON = 1e-6;

export type BrowserZoomUserProperties = {
  browser_zoom_percent: number;
  device_pixel_ratio: number;
};

const fallbackProperties: BrowserZoomUserProperties = {
  browser_zoom_percent: 100,
  device_pixel_ratio: 1
};

function isPositiveFinite(value: number): boolean {
  return Number.isFinite(value) && value > 0;
}

export function roundDevicePixelRatio(devicePixelRatio: number): number {
  const dpr = isPositiveFinite(devicePixelRatio) ? devicePixelRatio : 1;
  return Math.round(dpr * 100) / 100;
}

/**
 * Approximate Ctrl/Cmd page zoom as an integer percent (100 = 100%).
 *
 * Chromium and Safari fold OS display scale into `devicePixelRatio`, so DPR 2
 * is typically Retina at 100% zoom, not a 1x panel at 200%. This unwraps DPR
 * against common OS scales and Chrome zoom steps. Cost is reconstruction error
 * first, then a mix of OS-scale rank and distance from 100% zoom so
 * Retina-at-150% (DPR 3) beats 3x-at-100%, while 1x-at-100% still beats
 * 2x-at-50%. That also labels typical iPhone DPR 3 as 150% and Windows 250%
 * scale as 125%; `device_pixel_ratio` is the unambiguous companion. Pinch-zoom
 * is applied via `visualViewport.scale` (usually 1 on desktop even when chrome
 * zoom is not 100%).
 */
export function estimateBrowserZoomPercent(devicePixelRatio: number, visualViewportScale = 1): number {
  const dpr = isPositiveFinite(devicePixelRatio) ? devicePixelRatio : 1;
  const pinch = isPositiveFinite(visualViewportScale) ? visualViewportScale : 1;

  let bestZoomPercent = 100;
  let bestCost = Infinity;

  COMMON_OS_SCALES.forEach((osScale, osRank) => {
    for (const zoomPercent of BROWSER_ZOOM_PERCENTS) {
      const error = Math.abs(osScale * (zoomPercent / 100) - dpr);
      const cost = error * DPR_ERROR_WEIGHT + Math.abs(zoomPercent - 100) + osRank * OS_RANK_WEIGHT;
      const closerTo100 = Math.abs(zoomPercent - 100) < Math.abs(bestZoomPercent - 100);

      if (cost < bestCost - COST_TIE_EPSILON || (Math.abs(cost - bestCost) <= COST_TIE_EPSILON && closerTo100)) {
        bestCost = cost;
        bestZoomPercent = zoomPercent;
      }
    }
  });

  return Math.round(bestZoomPercent * pinch);
}

export function getBrowserZoomUserProperties(): BrowserZoomUserProperties {
  if (typeof window === "undefined") {
    return fallbackProperties;
  }

  const devicePixelRatio = window.devicePixelRatio || 1;
  const visualViewportScale = window.visualViewport?.scale ?? 1;

  return {
    browser_zoom_percent: estimateBrowserZoomPercent(devicePixelRatio, visualViewportScale),
    device_pixel_ratio: roundDevicePixelRatio(devicePixelRatio)
  };
}
